import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FolderPlusIcon } from 'lucide-react';
import { saveWidgetArtifact } from '@/entities/artifact';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useSaveToProjectEnabled } from '@/shared/hooks';
import type { ActionButton } from '@/shared/ui/SidePanelWrapper';
import { SaveToProjectPopover } from './SaveToProjectPopover';
import { useTranslation } from 'react-i18next';

/**
 * Builds the shared SidePanel "Save to project" header action used by the create
 * widgets (copy/image/video). Encapsulates the gating (flag + workspace + live
 * source artifact) and the popover/button block so each widget stays a one-liner.
 *
 * Returns `null` when the action shouldn't render, so callers spread it straight
 * into their `actions` array: `[infoAction, ...(saveAction ? [saveAction] : [])]`.
 *
 * `kind` selects the backend save-widget registry entry (`copy`/`image`/`video`).
 */
export function useSaveToProjectAction(
  kind: string,
  sourceArtifactId: string | undefined,
): ActionButton | null {
  const { t } = useTranslation('common');

  const saveEnabled = useSaveToProjectEnabled();
  const workspaceId = useCurrentWorkspaceId();

  if (!saveEnabled || !workspaceId || !sourceArtifactId) {
    return null;
  }

  return {
    type: 'custom',
    component: (
      /* Tooltip scoped to the trigger button. A SidePanelHeader action-level `tooltip` would wrap
         the whole popover, so hovering its items would surface this tooltip too. The trigger is
         nested inside the popover's own trigger, which is why only `TooltipTrigger` is passed as
         the popover child — a provider or root renders no element for the popover to attach to. */
      <TooltipProvider>
        <Tooltip>
          <SaveToProjectPopover
            resourceId={sourceArtifactId}
            workspaceId={workspaceId}
            onMintArtifact={() => saveWidgetArtifact(kind, workspaceId, sourceArtifactId)}
          >
            <TooltipTrigger asChild>
              <Button
                aria-label={t('ui.useSaveToProjectAction.saveToProject')}
                size="icon-sm"
                variant="ghost"
              >
                <FolderPlusIcon />
              </Button>
            </TooltipTrigger>
          </SaveToProjectPopover>
          <TooltipContent side="bottom">
            {t('ui.useSaveToProjectAction.saveToProject')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  };
}
